import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "selected", "selectedName", "selectedEmr", "form", "searchInput", "searchSpinner", "searchIcon", "loading", "searchResults"]
  static values = {
    selectedPatientId: String,
    selectedPatientName: String,
    selectedPatientEmr: String
  }

  connect() {
    this.searchTimeout = null
    this.isSearching = false
    console.log("Patient search frame controller connected")

    // Listen for Turbo Frame events
    this.element.addEventListener('turbo:before-fetch-request', this.handleBeforeFetchRequest.bind(this))
    this.element.addEventListener('turbo:frame-load', this.handleFrameLoad.bind(this))

    // Handle pre-selected patient
    this.handlePreSelectedPatient()

    // Ensure event listeners are set up
    this.setupEventListeners()
  }

  handlePreSelectedPatient() {
    // Check if we have a pre-selected patient from the server
    if (this.selectedPatientIdValue && this.selectedPatientNameValue) {
      console.log("Setting up pre-selected patient:", this.selectedPatientNameValue)

      // Set the hidden input value in the main form
      const mainForm = document.querySelector('form[action*="uploads"]')
      if (mainForm) {
        let patientIdInput = mainForm.querySelector('input[name="upload_session[patient_id]"]')
        if (!patientIdInput) {
          patientIdInput = document.createElement('input')
          patientIdInput.type = 'hidden'
          patientIdInput.name = 'upload_session[patient_id]'
          mainForm.appendChild(patientIdInput)
        }
        patientIdInput.value = this.selectedPatientIdValue
      }

      // Handle EMR checkbox for pre-selected patient
      this.handleEmrCheckbox(this.selectedPatientEmrValue)
    }
  }

  setupEventListeners() {
    // Find the search input and ensure events are bound
    const searchInput = this.element.querySelector('input[name="patient_query"]')
    if (searchInput) {
      console.log("Setting up search input listeners")

      // Remove existing listeners to prevent duplicates
      searchInput.removeEventListener('input', this.boundSearchHandler)
      searchInput.removeEventListener('change', this.boundChangeHandler)

      // Create bound handlers
      this.boundSearchHandler = this.search.bind(this)
      this.boundChangeHandler = this.change.bind(this)

      // Add new listeners
      searchInput.addEventListener('input', this.boundSearchHandler)
      searchInput.addEventListener('change', this.boundChangeHandler)
      searchInput.addEventListener('compositionend', this.boundChangeHandler)
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  search(event) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Show loading state immediately for empty searches
    const query = event.target.value.trim()
    if (query.length === 0) {
      this.hideLoadingState()
      this.performSearch(event.target)
      return
    }

    // Show loading state while debouncing
    this.showLoadingState()

    // Set new timeout for debounced search with increased delay
    this.searchTimeout = setTimeout(() => {
      this.performSearch(event.target)
    }, 300) // Increased from 200ms to 300ms for better performance
  }

  // Handle change event for better mobile support
  change(event) {
    // Clear any pending search timeout and search immediately on change
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.performSearch(event.target)
  }

  showLoadingState() {
    if (this.hasSearchSpinnerTarget) {
      this.searchSpinnerTarget.classList.remove('hidden')
    }
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.add('hidden')
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden')
    }
  }

  hideLoadingState() {
    if (this.hasSearchSpinnerTarget) {
      this.searchSpinnerTarget.classList.add('hidden')
    }
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.remove('hidden')
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
    }
  }

  performSearch(inputElement) {
    const query = inputElement.value.trim()

    try {
      // Always trigger search regardless of length
      const form = inputElement.closest('form')
      if (form && form.isConnected) {
        // Update the input value in the form to ensure it's submitted
        const queryInput = form.querySelector('input[name="patient_query"]')
        if (queryInput) {
          queryInput.value = query
        }

        // Use Turbo's built-in form submission which handles Turbo Frames automatically
        form.requestSubmit()
      } else if (form && !form.isConnected) {
        console.log("Form submission canceled because the form is not connected")
        // Retry after a short delay to allow DOM to stabilize
        setTimeout(() => {
          const reconnectedForm = inputElement.closest('form')
          if (reconnectedForm && reconnectedForm.isConnected) {
            reconnectedForm.requestSubmit()
          }
        }, 50)
      }
    } catch (error) {
      console.error('Search error:', error)
      this.showErrorState()
    }
  }

  showErrorState() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.innerHTML = `
        <div class="text-center py-4 text-red-500">
          <p>Search failed. Please try again.</p>
        </div>
      `
    }
  }

  handleBeforeFetchRequest(event) {
    // Show loading state when request starts
    this.showLoadingState()
  }

  handleFrameLoad(event) {
    // Hide loading state when frame loads
    this.hideLoadingState()
    this.isSearching = false

    // Re-setup event listeners after frame update
    this.setupEventListeners()
  }

  selectPatient(event) {
    const patientCard = event.currentTarget
    const patientId = patientCard.dataset.patientId
    const patientName = patientCard.dataset.patientName
    const patientEmr = patientCard.dataset.patientEmr || ''

    console.log('[Patient Search] Patient selected:', { patientId, patientName, patientEmr });

    // Hide search input and results
    this.inputTarget.style.display = 'none'
    this.resultsTarget.style.display = 'none'

    // Show selected patient
    this.selectedTarget.classList.remove('hidden')
    this.selectedNameTarget.textContent = patientName
    this.selectedEmrTarget.textContent = patientEmr ? `EMR ID: ${patientEmr}` : ''

    // Find the main form and add/update the hidden patient_id input
    const mainForm = document.querySelector('form[action*="uploads"]')
    if (mainForm) {
      let patientIdInput = mainForm.querySelector('input[name="upload_session[patient_id]"]')
      if (!patientIdInput) {
        patientIdInput = document.createElement('input')
        patientIdInput.type = 'hidden'
        patientIdInput.name = 'upload_session[patient_id]'
        mainForm.appendChild(patientIdInput)
      }
      patientIdInput.value = patientId
    }

    // Handle EMR checkbox for General Uploads patient
    this.handleEmrCheckbox(patientEmr)
  }

  clearSelection() {
    // Show search input and results again
    this.inputTarget.style.display = 'block'
    this.resultsTarget.style.display = 'block'

    // Hide selected patient display
    this.selectedTarget.classList.add('hidden')

    // Clear the search input
    const searchInput = this.hasSearchInputTarget ?
      this.searchInputTarget :
      this.inputTarget.querySelector('input[name="patient_query"]')

    if (searchInput) {
      searchInput.value = ''
      searchInput.focus()
    }

    // Reset loading states
    this.hideLoadingState()

    // Clear the hidden patient_id input in the main form
    const mainForm = document.querySelector('form[action*="uploads"]')
    if (mainForm) {
      const patientIdInput = mainForm.querySelector('input[name="upload_session[patient_id]"]')
      if (patientIdInput) {
        patientIdInput.value = ''
      }
    }

    // Re-enable EMR checkbox when patient is cleared
    this.handleEmrCheckbox(null)
  }

  handleEmrCheckbox(patientEmr) {
    // Find the EMR checkbox
    const emrCheckbox = document.getElementById('upload_to_emr')
    if (!emrCheckbox) return

    console.log('[Patient Search] Handling EMR checkbox for patient EMR:', patientEmr)

    // Disable checkbox for General Uploads patient
    if (patientEmr === 'GENERAL_UPLOADS') {
      console.log('[Patient Search] Disabling EMR checkbox for General Uploads patient')
      emrCheckbox.checked = false
      emrCheckbox.disabled = true

      // Add visual indication
      const wrapper = emrCheckbox.closest('.relative')
      if (wrapper) {
        wrapper.classList.add('opacity-50')
      }
    } else {
      console.log('[Patient Search] Enabling EMR checkbox for regular patient')
      emrCheckbox.disabled = false

      // Remove visual indication
      const wrapper = emrCheckbox.closest('.relative')
      if (wrapper) {
        wrapper.classList.remove('opacity-50')
      }
    }
  }
}